import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { Label } from '../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Plus, Search, Phone, Mail, MapPin, FileText, Edit, UserPlus, Users, CheckCircle2, ArrowRight, Download, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { usePermissions } from '../context/PermissionsContext';
import { DownloadWizard } from '../components/ui/download-wizard';
import { logger } from '../utils/logger';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function CustomersPage() {
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [formData, setFormData] = useState({
    full_name: '',
    phone: '',
    email: '',
    address: '',
    lead_id: ''
  });
  const [createMode, setCreateMode] = useState('scratch'); // 'scratch' or 'import'
  const [closedWonLeads, setClosedWonLeads] = useState([]);
  const [leadSearchQuery, setLeadSearchQuery] = useState('');
  const [selectedLead, setSelectedLead] = useState(null);
  const [downloadOpen, setDownloadOpen] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [customerToDelete, setCustomerToDelete] = useState(null);
  const navigate = useNavigate();
  
  const { role } = usePermissions();
  const isOwner = role === 'owner';

  // Customer download columns
  const CUSTOMER_DOWNLOAD_COLUMNS = [
    { key: "full_name", label: "Full Name" },
    { key: "phone", label: "Phone" },
    { key: "email", label: "Email" },
    { key: "address", label: "Address" },
    { key: "nid_passport", label: "NID/Passport" },
    { key: "source_lead_id", label: "Source Lead ID" },
    { key: "created_at", label: "Created At" },
    { key: "updated_at", label: "Updated At" }
  ];

  const fetchCustomers = async () => {
    try {
      const params = new URLSearchParams();
      if (searchQuery) params.append('search', searchQuery);

      const response = await fetch(
        `${API_URL}/api/financial/customers${params.toString() ? `?${params.toString()}` : ''}`,
        { credentials: 'include' }
      );

      if (response.ok) {
        const data = await response.json();
        setCustomers(data);
      } else {
        toast.error('Failed to load customers');
      }
    } catch (error) {
      logger.error('Error fetching customers:', error);
      toast.error('Error loading customers');
    } finally {
      setLoading(false);
    }
  };

  const fetchClosedWonLeads = async (search = '') => {
    try {
      const params = new URLSearchParams();
      if (search) params.append('search', search);

      const response = await fetch(
        `${API_URL}/api/financial/leads-for-customer${params.toString() ? `?${params.toString()}` : ''}`,
        { credentials: 'include' }
      );

      if (response.ok) {
        const data = await response.json();
        setClosedWonLeads(data);
      }
    } catch (error) {
      logger.error('Error fetching closed won leads:', error);
    }
  };



  useEffect(() => {
    fetchCustomers();
  }, [searchQuery]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (showCreateDialog && createMode === 'import') {
      fetchClosedWonLeads(leadSearchQuery);
    }
  }, [showCreateDialog, createMode, leadSearchQuery]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSelectLead = (lead) => {
    setSelectedLead(lead);
    setFormData({
      full_name: lead.full_name || '',
      phone: lead.phone || '',
      email: lead.email || '',
      address: '',
      lead_id: lead.lead_id
    });
  };

  const handleCreateCustomer = async (e) => {
    e.preventDefault();
    
    if (!formData.full_name || !formData.phone) {
      toast.error('Full name and phone are required');
      return;
    }

    try {
      const response = await fetch(`${API_URL}/api/financial/customers`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        const message = createMode === 'import' 
          ? 'Customer imported from lead successfully' 
          : 'Customer created successfully';
        toast.success(message);
        setShowCreateDialog(false);
        resetCreateForm();
        fetchCustomers();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to create customer');
      }
    } catch (error) {
      logger.error('Error creating customer:', error);
      toast.error('Error creating customer');
    }
  };

  const resetCreateForm = () => {
    setFormData({ full_name: '', phone: '', email: '', address: '', lead_id: '' });
    setCreateMode('scratch');
    setSelectedLead(null);
    setLeadSearchQuery('');
  };

  const handleUpdateCustomer = async (e) => {
    e.preventDefault();

    try {
      const response = await fetch(`${API_URL}/api/financial/customers/${selectedCustomer.customer_id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          full_name: formData.full_name,
          phone: formData.phone,
          email: formData.email,
          address: formData.address
        })
      });

      if (response.ok) {
        toast.success('Customer updated successfully');
        setShowEditDialog(false);
        setSelectedCustomer(null);
        setFormData({ full_name: '', phone: '', email: '', address: '', lead_id: '' });
        fetchCustomers();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to update customer');
      }
    } catch (error) {
      logger.error('Error updating customer:', error);
      toast.error('Error updating customer');
    }
  };

  const openEditDialog = (customer) => {
    setSelectedCustomer(customer);
    setFormData({
      full_name: customer.full_name || '',
      phone: customer.phone || '',
      email: customer.email || '',
      address: customer.address || '',
      lead_id: ''
    });
    setShowEditDialog(true);
  };

  // Delete customer handler
  const handleDeleteCustomer = async () => {
    if (!customerToDelete) return;
    
    try {
      const response = await fetch(`${API_URL}/api/financial/customers/${customerToDelete.customer_id}`, {
        method: 'DELETE',
        credentials: 'include'
      });

      if (response.ok) {
        toast.success('Customer deleted successfully');
        fetchCustomers();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to delete customer');
      }
    } catch (error) {
      logger.error('Error deleting customer:', error);
      toast.error('Error deleting customer');
    } finally {
      setDeleteConfirmOpen(false);
      setCustomerToDelete(null);
    }
  };

  // Download handler
  const handleDownload = async (selectedColumns) => {
    try {
      const response = await fetch(`${API_URL}/api/financial/customers/download`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ columns: selectedColumns }),
        credentials: 'include'
      });
      
      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `customers_export_${new Date().toISOString().slice(0,10)}.csv`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
        toast.success('Download started');
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Download failed');
      }
    } catch (error) {
      logger.error('Error downloading:', error);
      toast.error('Download failed');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gold"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="customers-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-heading font-bold text-slate-900">Customers</h1>
          <p className="text-slate-600 font-body">Manage financial customers</p>
        </div>
        
        <div className="flex items-center gap-2">
          {/* Download Button - Owner Only */}
          {isOwner && (
            <Button
              variant="outline"
              onClick={() => setDownloadOpen(true)}
              data-testid="download-customers-btn"
            >
              <Download className="h-4 w-4 mr-2" />
              Download
            </Button>
          )}
          
          <Dialog open={showCreateDialog} onOpenChange={(open) => {
            setShowCreateDialog(open);
            if (!open) resetCreateForm();
          }}>
            <DialogTrigger asChild>
              <Button className="bg-gold hover:bg-gold/90 text-slate-900" data-testid="create-customer-btn">
                <Plus className="h-4 w-4 mr-2" />
                New Customer
              </Button>
            </DialogTrigger>
          <DialogContent className="sm:max-w-lg max-h-[85vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Create New Customer</DialogTitle>
            </DialogHeader>
            
            <Tabs value={createMode} onValueChange={(v) => {
              setCreateMode(v);
              setSelectedLead(null);
              setFormData({ full_name: '', phone: '', email: '', address: '', lead_id: '' });
            }}>
              <TabsList className="grid w-full grid-cols-2 mb-4">
                <TabsTrigger value="scratch" className="flex items-center gap-2">
                  <UserPlus className="h-4 w-4" />
                  Create New
                </TabsTrigger>
                <TabsTrigger value="import" className="flex items-center gap-2">
                  <Users className="h-4 w-4" />
                  Import from Lead
                </TabsTrigger>
              </TabsList>
              
              {/* Create from Scratch */}
              <TabsContent value="scratch">
                <form onSubmit={handleCreateCustomer} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="full_name">Full Name *</Label>
                    <Input
                      id="full_name"
                      value={formData.full_name}
                      onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
                      placeholder="Enter full name"
                      required
                      data-testid="customer-name-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="phone">Phone *</Label>
                    <Input
                      id="phone"
                      value={formData.phone}
                      onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                      placeholder="Enter phone number"
                      required
                      data-testid="customer-phone-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="email">Email</Label>
                    <Input
                      id="email"
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      placeholder="Enter email (optional)"
                      data-testid="customer-email-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="address">Address</Label>
                    <Input
                      id="address"
                      value={formData.address}
                      onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                      placeholder="Enter address (optional)"
                      data-testid="customer-address-input"
                    />
                  </div>
                  <div className="flex justify-end gap-2 pt-2">
                    <Button type="button" variant="outline" onClick={() => setShowCreateDialog(false)}>
                      Cancel
                    </Button>
                    <Button type="submit" className="bg-gold hover:bg-gold/90 text-slate-900" data-testid="save-customer-btn">
                      Create Customer
                    </Button>
                  </div>
                </form>
              </TabsContent>
              
              {/* Import from Closed Won Lead */}
              <TabsContent value="import">
                <div className="space-y-4">
                  {!selectedLead ? (
                    <>
                      <div className="p-3 bg-blue-50 rounded-lg border border-blue-200">
                        <p className="text-sm text-blue-800">
                          <CheckCircle2 className="h-4 w-4 inline mr-1" />
                          Import customer data from a <strong>Closed Won</strong> lead. All contact information will be auto-filled.
                        </p>
                      </div>
                      
                      <div className="relative">
                        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-slate-400" />
                        <Input
                          placeholder="Search leads by name, phone, or email..."
                          value={leadSearchQuery}
                          onChange={(e) => setLeadSearchQuery(e.target.value)}
                          className="pl-10"
                          data-testid="lead-search-input"
                        />
                      </div>
                      
                      <div className="max-h-64 overflow-y-auto border rounded-lg divide-y">
                        {closedWonLeads.length === 0 ? (
                          <div className="p-4 text-center text-slate-500">
                            No Closed Won leads found
                          </div>
                        ) : (
                          closedWonLeads.map((lead) => (
                            <button
                              key={lead.lead_id}
                              type="button"
                              className={`w-full p-3 text-left hover:bg-slate-50 transition-colors ${lead.has_customer ? 'opacity-50' : ''}`}
                              onClick={() => !lead.has_customer && handleSelectLead(lead)}
                              disabled={lead.has_customer}
                              data-testid={`select-lead-${lead.lead_id}`}
                            >
                              <div className="flex items-center justify-between">
                                <div>
                                  <div className="font-medium text-slate-900">{lead.full_name}</div>
                                  <div className="text-sm text-slate-500 flex items-center gap-3">
                                    {lead.phone && (
                                      <span className="flex items-center gap-1">
                                        <Phone className="h-3 w-3" />
                                        {lead.phone}
                                      </span>
                                    )}
                                    {lead.email && (
                                      <span className="flex items-center gap-1">
                                        <Mail className="h-3 w-3" />
                                        {lead.email}
                                      </span>
                                    )}
                                  </div>
                                  {lead.product_interest && (
                                    <Badge variant="outline" className="mt-1 text-xs">
                                      {lead.product_interest}
                                    </Badge>
                                  )}
                                </div>
                                <div className="flex items-center gap-2">
                                  {lead.has_customer ? (
                                    <Badge className="bg-green-100 text-green-800 text-xs">
                                      Already imported
                                    </Badge>
                                  ) : (
                                    <ArrowRight className="h-4 w-4 text-slate-400" />
                                  )}
                                </div>
                              </div>
                            </button>
                          ))
                        )}
                      </div>
                    </>
                  ) : (
                    <form onSubmit={handleCreateCustomer} className="space-y-4">
                      <div className="p-3 bg-green-50 rounded-lg border border-green-200">
                        <p className="text-sm text-green-800">
                          <CheckCircle2 className="h-4 w-4 inline mr-1" />
                          Importing from lead: <strong>{selectedLead.full_name}</strong>
                        </p>
                        <Button 
                          type="button" 
                          variant="link" 
                          size="sm" 
                          className="text-green-700 p-0 h-auto"
                          onClick={() => {
                            setSelectedLead(null);
                            setFormData({ full_name: '', phone: '', email: '', address: '', lead_id: '' });
                          }}
                        >
                          Change selection
                        </Button>
                      </div>
                      
                      <div className="space-y-2">
                        <Label htmlFor="import_full_name">Full Name *</Label>
                        <Input
                          id="import_full_name"
                          value={formData.full_name}
                          onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
                          placeholder="Enter full name"
                          required
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="import_phone">Phone *</Label>
                        <Input
                          id="import_phone"
                          value={formData.phone}
                          onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                          placeholder="Enter phone number"
                          required
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="import_email">Email</Label>
                        <Input
                          id="import_email"
                          type="email"
                          value={formData.email}
                          onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                          placeholder="Enter email (optional)"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="import_address">Address</Label>
                        <Input
                          id="import_address"
                          value={formData.address}
                          onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                          placeholder="Enter address (optional)"
                        />
                      </div>
                      <div className="flex justify-end gap-2 pt-2">
                        <Button type="button" variant="outline" onClick={() => setShowCreateDialog(false)}>
                          Cancel
                        </Button>
                        <Button type="submit" className="bg-gold hover:bg-gold/90 text-slate-900">
                          Import as Customer
                        </Button>
                      </div>
                    </form>
                  )}
                </div>
              </TabsContent>
            </Tabs>
          </DialogContent>
        </Dialog>
        </div>
      </div>

      {/* Search */}
      <Card>
        <CardContent className="pt-6">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-slate-400" />
            <Input
              placeholder="Search by name, phone, or email..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10"
              data-testid="customer-search-input"
            />
          </div>
        </CardContent>
      </Card>

      {/* Customers Table */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg font-body">
            {customers.length} Customer{customers.length !== 1 ? 's' : ''}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {customers.length === 0 ? (
            <div className="text-center py-8 text-slate-500">
              No customers found
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Phone</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead>Contracts</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {customers.map((customer) => (
                  <TableRow key={customer.customer_id} data-testid={`customer-row-${customer.customer_id}`}>
                    <TableCell className="font-medium">{customer.full_name}</TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1 text-sm">
                        <Phone className="h-3 w-3 text-slate-400" />
                        {customer.phone}
                      </div>
                    </TableCell>
                    <TableCell>
                      {customer.email ? (
                        <div className="flex items-center gap-1 text-sm">
                          <Mail className="h-3 w-3 text-slate-400" />
                          {customer.email}
                        </div>
                      ) : (
                        <span className="text-slate-400 text-sm">-</span>
                      )}
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline">
                        <FileText className="h-3 w-3 mr-1" />
                        {customer.contracts_count || 0}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => openEditDialog(customer)}
                          data-testid={`edit-customer-${customer.customer_id}`}
                        >
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => navigate(`/finance/contracts?customer_id=${customer.customer_id}`)}
                          data-testid={`view-contracts-${customer.customer_id}`}
                        >
                          <FileText className="h-4 w-4 mr-1" />
                          Contracts
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            setCustomerToDelete(customer);
                            setDeleteConfirmOpen(true);
                          }}
                          className="text-red-500 hover:text-red-700 hover:bg-red-50"
                          data-testid={`delete-customer-${customer.customer_id}`}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Edit Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Edit Customer</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleUpdateCustomer} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="edit_full_name">Full Name *</Label>
              <Input
                id="edit_full_name"
                value={formData.full_name}
                onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
                placeholder="Enter full name"
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit_phone">Phone *</Label>
              <Input
                id="edit_phone"
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                placeholder="Enter phone number"
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit_email">Email</Label>
              <Input
                id="edit_email"
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                placeholder="Enter email (optional)"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit_address">Address</Label>
              <Input
                id="edit_address"
                value={formData.address}
                onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                placeholder="Enter address (optional)"
              />
            </div>
            <div className="flex justify-end gap-2">
              <Button type="button" variant="outline" onClick={() => setShowEditDialog(false)}>
                Cancel
              </Button>
              <Button type="submit" className="bg-gold hover:bg-gold/90 text-slate-900">
                Update Customer
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteConfirmOpen} onOpenChange={setDeleteConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Customer</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete <strong>{customerToDelete?.full_name}</strong>?
              <br /><br />
              This action cannot be undone. Customers with contracts or bookings cannot be deleted.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setCustomerToDelete(null)}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteCustomer}
              className="bg-red-600 hover:bg-red-700"
            >
              Delete Customer
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Download Wizard - Owner Only */}
      {isOwner && (
        <DownloadWizard
          open={downloadOpen}
          onOpenChange={setDownloadOpen}
          title="Download Customers"
          description="Select the columns you want to include in the customers export."
          columns={CUSTOMER_DOWNLOAD_COLUMNS}
          onDownload={handleDownload}
          defaultSelected={CUSTOMER_DOWNLOAD_COLUMNS.map(c => c.key)}
        />
      )}
    </div>
  );
}
